import React, { useEffect } from "react";
import SendbirdChat, { SendbirdUser } from "../../components/sendbird/chat";
import { Member } from "@sendbird/chat/groupChannel";
import { BreadcrumbItem } from "../../components/navigation/types/BreadcrumbItem";
import BreadcrumbsNav from "../../components/navigation/breadcrumbs-nav/BreadcrumbsNav";
import { BreadCrumbWrapper, VipCoachingPageWrapper, VipCoachPage, Title, HelloMessageCoach, MobileChatIndicatorWrapper, PageSpinnerWrapper, PageSpinner, CoachChatTitle, VipCoachWrapper } from "./index.styled";
import PageHelper from "./pageHelper";
import SubscriberPage from "../../components/sendbird/subscriberPage";
import CoachPage from "../../components/sendbird/coachPage";
import SupportPage from "../../components/sendbird/supportPage";
import { getUser } from "../../components/sendbird/sendbird.hooks";
import Survey from "./survey";
import ChatIndicator from "../../components/sendbird/subscriberPage/components/chatIndicator";
import PlayVideoModal from "../../components/sendbird/components/playVideoModal";
import { pgVIPHelpSwing } from "../../components/mixPanelEvents/mixPanelEvents";

export type VipPageView = 'init' | 'chat' | 'onboarding';

const VipCoaching: React.FC = () => {
    const [user, setUser] = React.useState<SendbirdUser | null>(null);
    const PageTitle = user?.user_role === 'support' ? "Support Chat" : "VIP Coaching";
    const breadcrumbItems: readonly BreadcrumbItem[] = [
        { label: PageTitle, href: "/vip-coaching" },
    ];
    const [isLoading, setIsLoading] = React.useState(true);
    const [view, setView] = React.useState<VipPageView>('init');
    const [coach, setCoach] = React.useState<Member | null>(null);
    const [anyMessageSent, setAnyMessageSent] = React.useState(false);
    const [uploadesIndicator, setUploadsIndicator] = React.useState<'my_uploads' | 'sent_from_coach'>('my_uploads');
    const [unreadCount, setUnreadCount] = React.useState<number>(0);
    const [helperModalOpen, setHelperModalOpen] = React.useState(false);
    const [isInactiveChannel, setIsInactiveChannel] = React.useState(false);

    useEffect(() => {
        const skip = window.location.hash === '#chat';
        if (skip) setView('chat');
        getUser(skip)
            .then((res) => {
                if (res.data.success === false) {
                    window.location.href = 'https://members.performancegolf.com/?redirect_to=vip-coaching-benefits';
                    return;
                }
                if (res.data.boarding && window.pgUser.products.includes("2")) {
                    setView('onboarding');
                    return;
                }
                const userData = res.data as SendbirdUser;
                userData.user_role = res.data?.isCoach ? 'coach' : res.data?.isSupport ? 'support' : 'subscriber';
                if (userData.user_role === 'subscriber' && !window.pgUser.products.includes("2")) {
                    window.location.href = 'https://members.performancegolf.com/?redirect_to=vip-coaching-benefits';
                    return;
                }
                setUser(userData);
            })
            .catch(() => {
                window.location.href = 'https://members.performancegolf.com/?redirect_to=vip-coaching-benefits';
            })
            .finally(() => setIsLoading(false));
    }, []);

    const handleSelectChannel = (url: string, isInactive = false) => {
        setCoach(null);
        setIsInactiveChannel(isInactive);
        setUser(prev => prev ? { ...prev, channel_url: url } : prev);
    };

    useEffect(() => {
        if (view === 'chat') {
            setUnreadCount(0);
        }
    }, [view]);

    return (
        <VipCoachWrapper>
            <VipCoachPage $pageState={view} $userRole={user?.user_role}>
                {isLoading && (
                    <PageSpinnerWrapper>
                        <PageSpinner />
                    </PageSpinnerWrapper>
                )}
                <VipCoachingPageWrapper $pageState={view} $userRole={user?.user_role}>
                    <BreadCrumbWrapper $pageState={view} $userRole={user?.user_role}>
                        <BreadcrumbsNav items={breadcrumbItems} separator="/" prefix="/" showIcon={true} />
                    </BreadCrumbWrapper>
                    {view === 'init' && user?.user_role === 'coach' && (
                        <HelloMessageCoach>
                            {window.pgUser.first_name ? `Hi, ${window.pgUser.first_name}` : ''}
                        </HelloMessageCoach>
                    )}
                    {user?.user_role !== 'coach' &&
                        <Title>
                            {PageTitle}
                        </Title>
                    }
                    {!isLoading && (user || view === 'onboarding') && (
                        <>
                            {(user?.user_role === 'subscriber' || view === 'onboarding') && (
                                <>
                                    {view === 'init' &&
                                        <MobileChatIndicatorWrapper>
                                            <ChatIndicator onOpenChat={() => setView('chat')} unreadCount={unreadCount} />
                                        </MobileChatIndicatorWrapper>
                                    }
                                    <PageHelper pageState={view} setUploadsIndicator={setUploadsIndicator} onHelperClick={() => {setHelperModalOpen(true); pgVIPHelpSwing({ Page: 'Messages From Coach', Type: 'Member' });}} />
                                </>
                            )}
                            {user?.user_role === 'coach' && user && (
                                <div style={{ display: view === 'init' ? 'block' : 'none' }}>
                                    <CoachPage user={user} onOpenChat={() => setView('chat')} setSelectedChannelUrl={handleSelectChannel} />
                                </div>
                            )}
                            {(view === 'init' && user && user.user_role !== 'coach') ? (
                                (() => {
                                    switch (user.user_role) {
                                        case 'support':
                                            return <SupportPage user={user} onChannelCreated={(url) => { handleSelectChannel(url); setView('chat'); }} />;
                                        default:
                                            return <SubscriberPage user={user} onOpenChat={() => setView('chat')} uploadesIndicator={uploadesIndicator} onUnreadCount={setUnreadCount} />;
                                    }
                                })()
                            ) : view === 'onboarding' ? (
                                <Survey onComplete={() => setView('chat')} setUser={setUser} />
                            ) : view !== 'init' ? (
                                <>
                                    {user?.user_role === 'coach' && <CoachChatTitle>Messages</CoachChatTitle>}
                                    <SendbirdChat
                                        pageTitle={PageTitle}
                                        coach={coach}
                                        setCoach={setCoach}
                                        anyMessageSent={anyMessageSent}
                                        setAnyMessageSent={setAnyMessageSent}
                                        setView={setView}
                                        user={user}
                                        isInactiveChannel={isInactiveChannel}
                                    />
                                </>
                            ) : null}
                        </>
                    )}
                </VipCoachingPageWrapper>
            </VipCoachPage>
            <PlayVideoModal
                open={helperModalOpen}
                iframeSrc="https://player.vimeo.com/video/891986556?h=440896f09b"
                onClose={() => setHelperModalOpen(false)}
            />
        </VipCoachWrapper>
    );
}

export default VipCoaching;